/**
 * createDrawerStyles — the scoped stylesheet for the CreateDrawer primitive,
 * extracted from the component so CreateDrawer.tsx stays within the size cap.
 * Injected once into <head> via {@link injectCreateDrawerStyles}.
 */

const CREATE_DRAWER_CSS = `
.nb-cdrawer{
  display:flex;flex-direction:column;height:100%;
  background:var(--nb-bg);color:var(--nb-ink);
  font-family:'DM Sans',system-ui,sans-serif;
  box-shadow:-12px 0 30px -8px rgba(0,0,0,.18);
  overflow:hidden;
}
.nb-cdrawer-hd{flex:0 0 auto;color:#fff;padding:20px 26px 18px;}
.nb-cdrawer-eyebrow{font-size:10px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;opacity:.7;}
.nb-cdrawer-title{
  margin:6px 0 0;font-family:'Hell or High Water','Fraunces',Georgia,serif;font-weight:400;
  text-transform:uppercase;font-size:26px;line-height:1.02;color:#fff;letter-spacing:.01em;
}
.nb-cdrawer-quote{display:flex;align-items:flex-start;gap:10px;margin-top:14px;}
.nb-cdrawer-quote-text{font-size:13px;font-style:italic;line-height:1.5;color:rgba(255,255,255,.92);}
.nb-cdrawer-quote-attr{font-size:9.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;opacity:.55;margin-top:8px;}

.nb-cdrawer-body{flex:1;min-height:0;display:flex;}
.nb-cdrawer-rail{
  flex:0 0 190px;border-right:1px solid var(--nb-border);background:var(--nb-surface);
  padding:22px 0 18px;position:relative;display:flex;flex-direction:column;
}
/* top + height are set inline from the step count so the line spans exactly
   node-1-center to node-N-center (no run-off into the empty rail below node N). */
.nb-cdrawer-track,.nb-cdrawer-fill{position:absolute;left:27px;width:2px;border-radius:2px;}
.nb-cdrawer-track{background:var(--nb-border);}
/* Fill shares the track geometry and scales from the top by the completed
   fraction (--cd-fill, 0..1) — exact node alignment, never past the last node. */
.nb-cdrawer-fill{background:var(--nb-primary);transform-origin:top;transform:scaleY(var(--cd-fill,0));transition:transform .4s ease;}
.nb-cdrawer-stepbtn{
  display:flex;align-items:center;gap:12px;padding:0 14px;text-align:left;
  background:transparent;border:0;cursor:pointer;font:inherit;color:inherit;position:relative;
}
.nb-cdrawer-node{
  width:28px;height:28px;border-radius:999px;flex:0 0 28px;z-index:1;
  display:flex;align-items:center;justify-content:center;
  background:var(--nb-bg);border:2px solid var(--nb-border-2);color:var(--nb-subtle);
  font-size:11px;font-weight:800;
}
.nb-cdrawer-stepbtn[data-state="active"] .nb-cdrawer-node{background:var(--nb-primary-soft);border-color:var(--nb-primary);color:var(--nb-primary);}
.nb-cdrawer-stepbtn[data-state="done"] .nb-cdrawer-node{background:var(--nb-primary);border-color:var(--nb-primary);color:#fff;}
.nb-cdrawer-steplabel{display:block;font-size:12.5px;font-weight:700;color:var(--nb-muted);}
.nb-cdrawer-stepbtn[data-state="active"] .nb-cdrawer-steplabel{color:var(--nb-ink);}
.nb-cdrawer-stephint{display:block;font-size:9.5px;color:var(--nb-subtle);}
.nb-cdrawer-railnote{margin-top:auto;padding:12px 14px 0;font-size:9.5px;color:var(--nb-faint);line-height:1.5;}

.nb-cdrawer-content{flex:1;min-width:0;overflow:auto;padding:20px 24px;}
.nb-cdrawer-ft{
  flex:0 0 auto;padding:14px 24px;border-top:1px solid var(--nb-border);
  background:var(--nb-surface);display:flex;align-items:center;gap:10px;
}
.nb-cdrawer-ftnote{flex:1;font-size:11px;color:var(--nb-subtle);line-height:1.4;}
.nb-cdrawer-btn{height:32px;padding:0 14px;border-radius:6px;cursor:pointer;font-family:'DM Sans',system-ui,sans-serif;font-size:13px;font-weight:700;background:transparent;border:1px solid transparent;color:var(--nb-muted);}
.nb-cdrawer-btn--secondary{border-color:var(--nb-border-2);}
.nb-cdrawer-btn--primary{background:var(--nb-primary);color:#fff;border-color:transparent;}

/* Phones: the rail becomes a strip of steps along the top, and the footer wraps. */
@media (max-width: 640px){
  .nb-cdrawer-body{flex-direction:column;}
  .nb-cdrawer-rail{flex:0 0 auto;border-right:0;border-bottom:1px solid var(--nb-border);padding:10px 8px;overflow-x:auto;}
  .nb-cdrawer-track,.nb-cdrawer-fill{display:none;}
  .nb-cdrawer-steps{flex-direction:row !important;gap:4px !important;}
  .nb-cdrawer-stepbtn{padding:0 8px;flex:0 0 auto;}
  .nb-cdrawer-stephint,.nb-cdrawer-railnote{display:none;}
  .nb-cdrawer-hd{padding:16px 16px 14px;}
  .nb-cdrawer-content{padding:16px;}
  .nb-cdrawer-ft{flex-wrap:wrap;padding:12px 16px;}
  .nb-cdrawer-ftnote{flex:1 1 100%;}
}

/* Optional scrim wrapper — fades in behind the sliding panel on open. */
.nb-cdrawer-scrim{opacity:1;}
@keyframes nbCdSlideIn   { from { transform: translateX(14px); } to { transform: none; } }
@keyframes nbCdPopIn     { from { transform: scale(.6); }       to { transform: scale(1); } }
@keyframes nbCdDrawerIn  { from { transform: translateX(100%); } to { transform: none; } }
@keyframes nbCdScrimIn   { from { opacity: 0; }                 to { opacity: 1; } }
@media (prefers-reduced-motion: no-preference){
  /* Panel slides in from the right edge on open; scrim fades behind it. */
  .nb-cdrawer{animation:nbCdDrawerIn .34s cubic-bezier(.32,.72,0,1) both;}
  .nb-cdrawer-scrim{animation:nbCdScrimIn .28s ease both;}
  .nb-cdrawer-slide{animation:nbCdSlideIn .28s ease both;}
  .nb-cdrawer-stepbtn[data-state="done"] .nb-cdrawer-node{animation:nbCdPopIn .3s cubic-bezier(.34,1.56,.64,1) both;}
}
`;

let styleInjected = false;

/** Inject the CreateDrawer stylesheet into <head> exactly once. */
export function injectCreateDrawerStyles(): void {
  if (styleInjected || typeof document === 'undefined') return;
  styleInjected = true;
  const el = document.createElement('style');
  el.setAttribute('data-ds', 'nb-CreateDrawer');
  el.textContent = CREATE_DRAWER_CSS;
  document.head.appendChild(el);
}
